import { useEffect, useState, useCallback, useRef } from "react";
import { useRecoilStateLoadable, useRecoilValueLoadable } from "recoil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EventRegister } from "react-native-event-listeners";
import { IEvidence, IUser } from "@interfaces/Interfaces";
import { evidenceAtom, userAtom } from "@state/State";
import * as DatabaseService from "@controllers/DatabaseService";
import DataController from "@controllers/DataController";

interface UseToggleAuditTag {
  optimisticAuditTag: boolean | undefined;
  toggleAuditTag: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => Promise<void>;
}

interface MutationVariables {
  user: IUser;
  evidence: IEvidence;
  newValue: boolean;
}

interface MutationContext {
  previousAllEvidence: IEvidence[] | null;
  previousOptimisticValue: boolean | undefined;
}

export const useToggleAuditTag = (evidence: IEvidence): UseToggleAuditTag => {
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const [allEvidence, setAllEvidence] = useRecoilStateLoadable<IEvidence[] | null>(evidenceAtom);
  const queryClient = useQueryClient();

  const [optimisticAuditTag, setOptimisticAuditTag] = useState<boolean | undefined>(evidence.addToHCPCAudit);
  const isMutationInProgress = useRef<boolean>(false);

  useEffect(() => {
    // Skip syncing if a mutation is in progress to prevent overwriting optimistic updates
    if (!isMutationInProgress.current) {
      setOptimisticAuditTag(evidence.addToHCPCAudit);
    }
  }, [evidence.addToHCPCAudit]);

  const mutationFn = async ({ user, evidence, newValue }: MutationVariables): Promise<boolean> => {
    const result = await DatabaseService.toggleAuditTagged(user, evidence, newValue);

    if (!result) {
      throw new Error("Failed to toggle audit tag");
    }

    return result;
  };

  const mutation = useMutation<boolean, Error, MutationVariables, MutationContext>({
    mutationFn,
    onMutate: async (variables) => {
      isMutationInProgress.current = true;

      const previousAllEvidence = allEvidence.state === "hasValue" ? structuredClone(allEvidence.contents) : null;
      const previousOptimisticValue = optimisticAuditTag;

      setOptimisticAuditTag(variables.newValue);

      if (allEvidence.state === "hasValue" && allEvidence.contents) {
        const _allEvidence = structuredClone(allEvidence.contents);
        const evidenceIndex = _allEvidence.findIndex(
          (evidenceItem: IEvidence) => evidenceItem.id === variables.evidence.id
        );

        if (evidenceIndex !== -1) {
          _allEvidence[evidenceIndex].addToHCPCAudit = variables.newValue;
        }

        setAllEvidence(_allEvidence);
      }

      return { previousAllEvidence, previousOptimisticValue };
    },
    onSuccess: async (data, variables) => {
      if (allEvidence.state === "hasValue" && allEvidence.contents) {
        const _allEvidence = structuredClone(allEvidence.contents);
        await DataController.saveEvidence(_allEvidence);
      }

      if (variables.user?.userData?.contactID) {
        queryClient.invalidateQueries({ queryKey: ["user-evidence", variables.user.userData.contactID] });
      }

      isMutationInProgress.current = false;
    },
    onError: (error, variables, context) => {
      if (context) {
        setAllEvidence(context.previousAllEvidence);
        setOptimisticAuditTag(context.previousOptimisticValue);
      }
      EventRegister.emit("evidence/audit-tag-error");

      isMutationInProgress.current = false;
    },
  });

  const toggleAuditTag = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();

      if (user.state !== "hasValue" || !user.contents) {
        EventRegister.emit("evidence/audit-tag-error");
        return;
      }

      const _evidence = structuredClone(evidence);
      const newAuditTagValue = !_evidence.addToHCPCAudit;

      mutation.mutate({
        user: user.contents,
        evidence: _evidence,
        newValue: newAuditTagValue,
      });
    },
    [evidence, user, mutation]
  );

  return {
    optimisticAuditTag,
    toggleAuditTag,
  };
};
